import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { Box, IconButton, Typography } from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import * as Mui from "@mui/material";
import {
  childBoxStyleSpaceBetween,
  doubleArrowStyle,
  drawerStyle,
  parentBoxStyle,
} from "services/constants";
import ContactFormPage from "pages/CRM/ContactFormPage";
import { crmContactColors } from "pages/CRM/contactFormStyles";

const crmFormDrawerStyle = {
  ...drawerStyle,
  "& .MuiDrawer-paper": {
    ...drawerStyle["& .MuiDrawer-paper"],
    width: { xs: "100vw", sm: "820px", md: "880px" },
    maxWidth: "100vw",
    backgroundColor: crmContactColors.page,
    boxShadow: "-18px 0 40px rgba(31, 41, 51, 0.18)",
  },
};

const ContactDrawerContext = createContext(null);

export function ContactDrawerProvider({ children }) {
  const [state, setState] = useState({ open: false, prefillPhone: "", onSaved: null });
  const [pageDrawerActive, setPageDrawerActive] = useState(false);

  const openCreateContactDrawer = useCallback((prefillPhone = "", onSaved = null) => {
    setState({ open: true, prefillPhone, onSaved });
  }, []);

  const closeContactDrawer = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const registerPageDrawer = useCallback((isActive) => {
    setPageDrawerActive(isActive);
  }, []);

  const handleSaved = useCallback(async () => {
    if (state.onSaved) {
      await state.onSaved();
    }
    closeContactDrawer();
  }, [state, closeContactDrawer]);

  const value = useMemo(
    () => ({ openCreateContactDrawer, registerPageDrawer }),
    [openCreateContactDrawer, registerPageDrawer]
  );

  const showGlobalDrawer = state.open && !pageDrawerActive;

  return (
    <ContactDrawerContext.Provider value={value}>
      {children}
      <Drawer
        anchor="right"
        open={showGlobalDrawer}
        onClose={closeContactDrawer}
        sx={crmFormDrawerStyle}
      >
        <Box sx={parentBoxStyle}>
          <Mui.Paper
            elevation={0}
            sx={{
              backgroundColor: "#fff",
              borderBottom: `1px solid ${crmContactColors.border}`,
              borderRadius: 0,
            }}
          >
            <Box sx={{ ...childBoxStyleSpaceBetween, px: 1.5, pt: 1 }}>
              <IconButton
                onClick={closeContactDrawer}
                sx={{
                  ...doubleArrowStyle,
                  color: crmContactColors.accentDark,
                  "&:hover": { backgroundColor: "rgba(132, 222, 184, 0.18)" },
                }}
              >
                <DoubleArrow />
              </IconButton>
            </Box>
            <Box sx={{ ml: 2.2, mr: 2, mb: 1.5, borderLeft: `4px solid ${crmContactColors.accent}`, pl: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: crmContactColors.text }}>
                Add Contact
              </Typography>
              <Typography variant="body2" sx={{ color: crmContactColors.muted }}>
                Create a contact with company, phone, email, address, and CRM details.
              </Typography>
            </Box>
          </Mui.Paper>
          <Box sx={{ flexGrow: 1, overflow: "auto", p: 2, pb: 0, backgroundColor: crmContactColors.page }}>
            {showGlobalDrawer && (
              <ContactFormPage
                key={`global-create-${state.prefillPhone}`}
                drawerMode
                hideHeader
                prefillPhone={state.prefillPhone}
                onClose={closeContactDrawer}
                onSaved={handleSaved}
              />
            )}
          </Box>
        </Box>
      </Drawer>
    </ContactDrawerContext.Provider>
  );
}

export function useContactDrawer() {
  const ctx = useContext(ContactDrawerContext);
  if (!ctx) {
    throw new Error("useContactDrawer must be used within a ContactDrawerProvider");
  }
  return ctx;
}
